import Phaser from "phaser";
import {
  LEVELS,
  TOTAL_LEVELS,
  movesOf,
  starsForShots,
  type LevelDef,
} from "./levels";
import { recordStars } from "./storage";
import { domText, domClickable, domEl, starsHTML } from "../../dom";

const PLAYER_R = 18;
const BALL_R = 10;
const MAX_DRAG = 130;
const POWER = 0.16;
const STILL_VEL = 0.15;

// Pool-like calibration. Low friction so players retain energy across the
// pitch — current attacker max travel ≈ |v0|/frictionAir = 130*0.16/0.01 ≈ 2080
// pixels, ~3× pitch height. Restitution is high so collisions transfer energy
// rather than dampening it.
const ATTACKER_FRICTION_AIR = 0.01;
const ATTACKER_RESTITUTION = 0.92;
const ATTACKER_FRICTION = 0.002;
const ATTACKER_DENSITY = 0.005;

const BALL_FRICTION_AIR = 0.012;
const BALL_RESTITUTION = 0.95;
const BALL_FRICTION = 0.002;
const BALL_DENSITY = 0.0008;

const DEFENDER_FRICTION_AIR = 0.025;
const DEFENDER_RESTITUTION = 0.85;
const DEFENDER_FRICTION = 0.002;
const DEFENDER_DENSITY = 0.006;

const COLOR_PITCH_A = 0x4a8d50;
const COLOR_PITCH_B = 0x437f48;
const COLOR_LINE = 0xffffff;
const COLOR_ATTACKER = 0xf5e6d3;
const COLOR_ATTACKER_RING = 0xd4a574;
const COLOR_ATTACKER_HEAD = 0x4a3a2a;
const COLOR_DEFENDER = 0x4a3a2a;
const COLOR_DEFENDER_RING = 0x6b5a47;
const COLOR_DEFENDER_HEAD = 0xc9b89a;
const COLOR_BALL = 0xffffff;
const COLOR_AIM = 0xffffff;
const COLOR_BALL_PREDICT = 0xffe5a0;

const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

type MImage = Phaser.Physics.Matter.Image;

interface AttackerState {
  piece: MImage;
  movesLeft: number;
  label: Phaser.GameObjects.Text;
}

export class FootballPlayScene extends Phaser.Scene {
  private level!: LevelDef;
  private ball!: MImage;
  private attackers: AttackerState[] = [];
  private defenders: MImage[] = [];
  private aimingPlayer: AttackerState | null = null;
  private aimStart = { x: 0, y: 0 };
  private aimGfx!: Phaser.GameObjects.Graphics;
  private aimEnd!: Phaser.GameObjects.Arc;
  private pitchGfx!: Phaser.GameObjects.Graphics;
  private goalGfx!: Phaser.GameObjects.Graphics;
  private hudGfx!: Phaser.GameObjects.Graphics;

  private goalWidth = 0;
  private goalLeft = 0;
  private goalRight = 0;

  private shotsTaken = 0;
  private over = false;
  private celebrating = false;

  private hudHeight = 56;

  constructor() {
    super("football-play");
  }

  init(data: { levelId?: number }) {
    const id = data?.levelId ?? 1;
    this.level = LEVELS.find((l) => l.id === id) ?? LEVELS[0];
    this.shotsTaken = 0;
    this.over = false;
    this.celebrating = false;
    this.attackers = [];
    this.defenders = [];
    this.aimingPlayer = null;
  }

  create() {
    this.makeTextures();
    this.pitchGfx = this.add.graphics();
    this.goalGfx = this.add.graphics();
    this.aimGfx = this.add.graphics().setDepth(20);
    this.aimEnd = this.add
      .circle(0, 0, 10, 0xffffff, 0)
      .setStrokeStyle(2.5, COLOR_AIM, 1)
      .setDepth(21)
      .setVisible(false);

    this.drawPitch();
    this.buildWalls();
    this.buildPieces();
    this.drawHud();

    this.input.on("pointerdown", this.onPointerDown, this);
    this.input.on("pointermove", this.onPointerMove, this);
    this.input.on("pointerup", this.onPointerUp, this);
    this.scale.on("resize", this.onResize, this);
  }

  update() {
    if (this.over) return;
    const b = this.ball.body as MatterJS.BodyType;
    if (b.position.y < -BALL_R * 2) this.scoreGoal();

    for (const a of this.attackers) {
      a.label.setPosition(a.piece.x, a.piece.y);
    }
  }

  shutdown() {
    this.scale.off("resize", this.onResize, this);
  }

  // --- pitch + pieces ---

  private makeTextures() {
    const playerTex = (
      key: string,
      body: number,
      ring: number,
      head: number,
    ) => {
      const r = PLAYER_R;
      const d = r * 2 + 4;
      const g = this.make.graphics({ x: 0, y: 0 }, false);
      g.fillStyle(ring, 1).fillCircle(d / 2, d / 2, r + 1);
      g.fillStyle(body, 1).fillCircle(d / 2, d / 2, r - 1);
      // Head: small inner circle, centred. Top-down view gives the piece a
      // player silhouette without breaking radial symmetry (so rotation under
      // physics doesn't read as the figure spinning).
      g.fillStyle(head, 1).fillCircle(d / 2, d / 2, r * 0.42);
      g.generateTexture(key, d, d);
      g.destroy();
    };
    playerTex("attacker", COLOR_ATTACKER, COLOR_ATTACKER_RING, COLOR_ATTACKER_HEAD);
    playerTex("defender", COLOR_DEFENDER, COLOR_DEFENDER_RING, COLOR_DEFENDER_HEAD);

    const bg = this.make.graphics({ x: 0, y: 0 }, false);
    const bd = BALL_R * 2 + 2;
    bg.fillStyle(0x222222, 1).fillCircle(bd / 2, bd / 2, BALL_R);
    bg.fillStyle(COLOR_BALL, 1).fillCircle(bd / 2, bd / 2, BALL_R - 1);
    bg.generateTexture("ball", bd, bd);
    bg.destroy();
  }

  private drawPitch() {
    const { width: w, height: h } = this.scale;
    const stripes = 10;
    const sh = h / stripes;
    const g = this.pitchGfx;
    g.clear();
    for (let i = 0; i < stripes; i++) {
      g.fillStyle(i % 2 === 0 ? COLOR_PITCH_A : COLOR_PITCH_B, 1);
      g.fillRect(0, i * sh, w, sh + 1);
    }
    const pad = 10;
    g.lineStyle(2, COLOR_LINE, 0.85);
    g.strokeRect(pad, pad, w - pad * 2, h - pad * 2);
    g.lineBetween(pad, h / 2, w - pad, h / 2);
    g.strokeCircle(w / 2, h / 2, Math.min(w, h) * 0.1);
    g.fillStyle(COLOR_LINE, 0.9).fillCircle(w / 2, h / 2, 3);

    const boxW = w * 0.6;
    const boxH = h * 0.18;
    g.strokeRect((w - boxW) / 2, pad, boxW, boxH);
    const sBoxW = w * 0.3;
    const sBoxH = h * 0.08;
    g.strokeRect((w - sBoxW) / 2, pad, sBoxW, sBoxH);

    const goalPct = this.level.goalWidthPct ?? 0.36;
    this.goalWidth = w * goalPct;
    this.goalLeft = (w - this.goalWidth) / 2;
    this.goalRight = (w + this.goalWidth) / 2;

    const gg = this.goalGfx;
    gg.clear();
    gg.lineStyle(5, COLOR_LINE, 1);
    gg.lineBetween(this.goalLeft, pad, this.goalRight, pad);
    gg.lineStyle(1, COLOR_LINE, 0.4);
    for (let x = this.goalLeft + 6; x < this.goalRight; x += 8) {
      gg.lineBetween(x, pad - 1, x, 0);
    }
  }

  private buildWalls() {
    const { width: w, height: h } = this.scale;
    const t = 40;
    const opts = { isStatic: true, restitution: 0.9, friction: 0.002 };
    this.matter.add.rectangle(w / 2, h + t / 2, w + t * 2, t, opts);
    this.matter.add.rectangle(-t / 2, h / 2, t, h + t * 2, opts);
    this.matter.add.rectangle(w + t / 2, h / 2, t, h + t * 2, opts);
    this.matter.add.rectangle(this.goalLeft / 2, -t / 2, this.goalLeft, t, opts);
    this.matter.add.rectangle(
      (w + this.goalRight) / 2,
      -t / 2,
      w - this.goalRight,
      t,
      opts,
    );
  }

  private buildPieces() {
    const { width: w, height: h } = this.scale;
    this.ball = this.matter.add
      .image(this.level.ballAt.x * w, this.level.ballAt.y * h, "ball", undefined, {
        shape: { type: "circle", radius: BALL_R },
        restitution: BALL_RESTITUTION,
        frictionAir: BALL_FRICTION_AIR,
        friction: BALL_FRICTION,
        density: BALL_DENSITY,
      })
      .setDepth(10);

    for (const spec of this.level.attackers) {
      const piece = this.matter.add
        .image(spec.x * w, spec.y * h, "attacker", undefined, {
          shape: { type: "circle", radius: PLAYER_R },
          restitution: ATTACKER_RESTITUTION,
          frictionAir: ATTACKER_FRICTION_AIR,
          friction: ATTACKER_FRICTION,
          density: ATTACKER_DENSITY,
        })
        .setDepth(10);
      const movesLeft = movesOf(spec);
      // Move count is one of the few labels that has to track a moving physics
      // body each frame, so this stays a Phaser canvas text object. Drawn over
      // the dark head — light text contrasts.
      const label = this.add
        .text(piece.x, piece.y, String(movesLeft), {
          fontFamily: FONT,
          fontSize: "18px",
          color: "#f5e6d3",
          fontStyle: "bold",
          resolution: 3,
        })
        .setOrigin(0.5)
        .setDepth(11);
      this.attackers.push({ piece, movesLeft, label });
    }

    for (const d of this.level.defenders) {
      const piece = this.matter.add
        .image(d.x * w, d.y * h, "defender", undefined, {
          shape: { type: "circle", radius: PLAYER_R },
          restitution: DEFENDER_RESTITUTION,
          frictionAir: DEFENDER_FRICTION_AIR,
          friction: DEFENDER_FRICTION,
          density: DEFENDER_DENSITY,
        })
        .setDepth(10);
      this.defenders.push(piece);
    }
  }

  // --- HUD ---

  private drawHud() {
    if (!this.hudGfx) this.hudGfx = this.add.graphics().setDepth(29);
    this.hudGfx.clear();
    const { width: w } = this.scale;
    this.hudGfx.fillStyle(0x000000, 0.18);
    this.hudGfx.fillRect(0, 0, w, this.hudHeight);

    domClickable(this, 30, 28, "←", "arc-hud-back", () =>
      this.scene.start("football"),
    );
    domText(this, w / 2, 28, `Level ${this.level.id}`, "arc-hud-title");
    if (this.level.hint) {
      domText(this, w / 2, this.scale.height - 22, this.level.hint, "arc-hud-hint");
    }
  }

  // --- input ---

  private isStill(p: MImage) {
    const b = p.body as MatterJS.BodyType;
    return Math.hypot(b.velocity.x, b.velocity.y) < STILL_VEL;
  }

  private onPointerDown = (p: Phaser.Input.Pointer) => {
    if (this.over) return;
    if (p.y < this.hudHeight) return;
    for (const a of this.attackers) {
      if (a.movesLeft <= 0) continue;
      const d = Phaser.Math.Distance.Between(p.x, p.y, a.piece.x, a.piece.y);
      if (d < PLAYER_R + 12 && this.isStill(a.piece)) {
        this.aimingPlayer = a;
        this.aimStart.x = p.x;
        this.aimStart.y = p.y;
        return;
      }
    }
  };

  private onPointerMove = (p: Phaser.Input.Pointer) => {
    if (!this.aimingPlayer) return;
    this.drawAim(p);
  };

  private onPointerUp = (p: Phaser.Input.Pointer) => {
    if (!this.aimingPlayer) return;
    const a = this.aimingPlayer;
    const dx = this.aimStart.x - p.x;
    const dy = this.aimStart.y - p.y;
    const len = Math.hypot(dx, dy);
    const capped = Math.min(len, MAX_DRAG);
    if (capped > 8) {
      const ratio = capped / len;
      a.piece.setVelocity(dx * ratio * POWER, dy * ratio * POWER);
      a.movesLeft -= 1;
      a.label.setText(String(a.movesLeft));
      if (a.movesLeft === 0) a.label.setAlpha(0.55);
      this.shotsTaken += 1;
      if (this.totalMovesLeft() === 0) this.scheduleOutOfShotsCheck();
    }
    this.aimingPlayer = null;
    this.aimGfx.clear();
    this.aimEnd.setVisible(false);
  };

  private totalMovesLeft() {
    return this.attackers.reduce((s, a) => s + a.movesLeft, 0);
  }

  // Clip a ray (start + t*direction) at the pitch boundary (axis-aligned
  // walls). Returns the largest t the attacker could travel before hitting a
  // wall — used to cap the aim line so it doesn't extend past the pitch.
  private wallLimit(ax: number, ay: number, ux: number, uy: number): number {
    const w = this.scale.width;
    const h = this.scale.height;
    const m = PLAYER_R + 2;
    let t = Infinity;
    if (ux > 1e-6) t = Math.min(t, (w - m - ax) / ux);
    if (ux < -1e-6) t = Math.min(t, (m - ax) / ux);
    if (uy > 1e-6) t = Math.min(t, (h - m - ay) / uy);
    if (uy < -1e-6) t = Math.min(t, (m - ay) / uy);
    return Math.max(0, t);
  }

  private drawAim(p: Phaser.Input.Pointer) {
    const a = this.aimingPlayer!.piece;
    this.aimGfx.clear();
    const dx = this.aimStart.x - p.x;
    const dy = this.aimStart.y - p.y;
    const len = Math.hypot(dx, dy);
    if (len < 4) {
      this.aimEnd.setVisible(false);
      return;
    }
    const capped = Math.min(len, MAX_DRAG);
    const speed = capped * POWER;
    const projDist = speed / ATTACKER_FRICTION_AIR;
    const ux = dx / len;
    const uy = dy / len;
    const power = capped / MAX_DRAG;
    const alpha = 0.5 + 0.45 * power;

    // Cap projected distance at the pitch boundary — avoids the aim line
    // extending off-pitch, which would lie about reachable destinations.
    const clipped = Math.min(projDist, this.wallLimit(a.x, a.y, ux, uy));

    const obstacles: Array<{ x: number; y: number; r: number; isBall: boolean }> = [
      { x: this.ball.x, y: this.ball.y, r: BALL_R, isBall: true },
      ...this.defenders.map((d) => ({ x: d.x, y: d.y, r: PLAYER_R, isBall: false })),
      ...this.attackers
        .filter((o) => o.piece !== a)
        .map((o) => ({ x: o.piece.x, y: o.piece.y, r: PLAYER_R, isBall: false })),
    ];

    let hit:
      | { t: number; x: number; y: number; isBall: boolean; cx: number; cy: number; cr: number }
      | null = null;

    for (const obs of obstacles) {
      const combinedR = obs.r + PLAYER_R;
      const ddx = a.x - obs.x;
      const ddy = a.y - obs.y;
      const b_coef = 2 * (ddx * ux + ddy * uy);
      const c_coef = ddx * ddx + ddy * ddy - combinedR * combinedR;
      const disc = b_coef * b_coef - 4 * c_coef;
      if (disc < 0) continue;
      const t1 = (-b_coef - Math.sqrt(disc)) / 2;
      if (t1 < 0 || t1 > clipped) continue;
      if (!hit || t1 < hit.t) {
        hit = {
          t: t1,
          x: a.x + ux * t1,
          y: a.y + uy * t1,
          isBall: obs.isBall,
          cx: obs.x,
          cy: obs.y,
          cr: combinedR,
        };
      }
    }

    const tx = hit ? hit.x : a.x + ux * clipped;
    const ty = hit ? hit.y : a.y + uy * clipped;

    this.drawDottedLine(this.aimGfx, a.x, a.y, tx, ty, 7, 7, 3, COLOR_AIM, alpha);
    this.aimEnd.setPosition(tx, ty).setVisible(true).setAlpha(alpha);

    if (hit && hit.isBall) {
      const nx = (hit.cx - hit.x) / hit.cr;
      const ny = (hit.cy - hit.y) / hit.cr;
      const lineLen = 110 + 110 * power;
      const bx = hit.cx + nx * lineLen;
      const by = hit.cy + ny * lineLen;
      this.drawDottedLine(
        this.aimGfx,
        hit.cx,
        hit.cy,
        bx,
        by,
        5,
        5,
        2,
        COLOR_BALL_PREDICT,
        alpha * 0.85,
      );
    }
  }

  private drawDottedLine(
    g: Phaser.GameObjects.Graphics,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    dotLen: number,
    gap: number,
    width: number,
    color: number,
    alpha: number,
  ) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy);
    if (len === 0) return;
    const nx = dx / len;
    const ny = dy / len;
    g.lineStyle(width, color, alpha);
    let drawn = 0;
    while (drawn < len) {
      const seg = Math.min(dotLen, len - drawn);
      g.lineBetween(
        x1 + nx * drawn,
        y1 + ny * drawn,
        x1 + nx * (drawn + seg),
        y1 + ny * (drawn + seg),
      );
      drawn += seg + gap;
    }
  }

  // --- end states ---

  private scheduleOutOfShotsCheck() {
    this.time.delayedCall(800, () => this.tryEndAfterSettle());
  }

  private tryEndAfterSettle(attemptsLeft = 30) {
    if (this.over || this.celebrating) return;
    if ((this.ball.body as MatterJS.BodyType).position.y < -BALL_R * 2) return;
    const allStill = this.everythingStill();
    if (!allStill && attemptsLeft > 0) {
      this.time.delayedCall(200, () => this.tryEndAfterSettle(attemptsLeft - 1));
      return;
    }
    this.outOfShots();
  }

  private everythingStill(): boolean {
    const items: MImage[] = [
      this.ball,
      ...this.attackers.map((a) => a.piece),
      ...this.defenders,
    ];
    for (const it of items) {
      const b = it.body as MatterJS.BodyType;
      if (Math.hypot(b.velocity.x, b.velocity.y) > STILL_VEL) return false;
    }
    return true;
  }

  private scoreGoal() {
    if (this.celebrating) return;
    this.celebrating = true;
    this.over = true;
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(30);

    const stars = starsForShots(this.level, this.shotsTaken);
    recordStars(this.level.id, stars);

    this.showLevelComplete(stars);
  }

  private outOfShots() {
    if (this.over || this.celebrating) return;
    this.over = true;
    this.showRetry();
  }

  // --- overlays ---

  private showLevelComplete(stars: number) {
    const isLast = this.level.id >= TOTAL_LEVELS;
    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;

    this.spawnDim();
    domText(this, cx, cy - 100, "GOAL", "arc-banner", 46);
    domEl(this, cx, cy - 38, starsHTML(stars), 46);
    domText(
      this,
      cx,
      cy + 16,
      `Solved in ${this.shotsTaken} ${this.shotsTaken === 1 ? "shot" : "shots"}`,
      "arc-subtle",
      46,
    );

    if (isLast) {
      this.makeDomButton(cx, cy + 86, "Back to levels", true, () =>
        this.scene.start("football"),
      );
    } else {
      this.makeDomButton(cx - 78, cy + 86, "Levels", false, () =>
        this.scene.start("football"),
      );
      this.makeDomButton(cx + 78, cy + 86, "Next →", true, () =>
        this.scene.start("football-play", { levelId: this.level.id + 1 }),
      );
    }
  }

  private showRetry() {
    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;

    this.spawnDim();
    domText(this, cx, cy - 70, "Out of shots", "arc-banner-small", 46);
    domText(this, cx, cy - 28, "Try a different angle.", "arc-subtle", 46);
    this.makeDomButton(cx - 78, cy + 60, "Levels", false, () =>
      this.scene.start("football"),
    );
    this.makeDomButton(cx + 78, cy + 60, "Retry", true, () =>
      this.scene.start("football-play", { levelId: this.level.id }),
    );
  }

  private spawnDim() {
    const { width: w, height: h } = this.scale;
    this.add.rectangle(w / 2, h / 2, w * 2, h * 2, 0x000000, 0.55).setDepth(45);
  }

  private makeDomButton(
    x: number,
    y: number,
    label: string,
    primary: boolean,
    onClick: () => void,
  ) {
    const btn = document.createElement("button");
    btn.className = `arc-btn${primary ? " arc-btn-primary" : ""}`;
    btn.textContent = label;
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    });
    return this.add.dom(x, y, btn).setOrigin(0.5).setDepth(47);
  }

  // --- resize ---

  private onResize = () => {
    this.drawPitch();
  };
}
